import { Task } from '../types/Task';
import { PriorityBadge } from './PriorityBadge';
import { formatTaskDate, formatTaskDateTime, isTaskDueDateOverdue } from '../utils/taskDueDate';
import { TaskActionButtons } from './TaskActionButtons';

interface TaskCardProps {
  task: Task;
  onDelete: (id: number) => void;
  onStatusChange: (id: number, status: string) => void;
  onViewDetails: (task: Task) => void;
  onEdit: (task: Task) => void;
}

/**
 * TaskCard Component
 * Mobile-responsive card view for displaying tasks
 * Displayed on small screens when table view is hidden
 */
export const TaskCard: React.FC<TaskCardProps> = ({ task, onDelete, onStatusChange, onViewDetails, onEdit }) => {
  const isOverdue = isTaskDueDateOverdue(task.dueDate);

  return (
    <div className="task-card">
      <div className="task-card-header">
        <div className="task-card-title">{task.title}</div>
        <div className="task-card-status">
          <select
            value={task.status}
            onChange={(e) => onStatusChange(task.id!, e.target.value)}
            className="status-dropdown"
          >
            <option value="TODO">TODO</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="DONE">Done</option>
          </select>
        </div>
      </div>

      {task.description && (
        <div className="task-card-description collapsed">
          {task.description}
        </div>
      )}

      <div className="task-card-meta">
        <div className="task-card-priority">
          <span className="task-card-meta-label">Priority</span>
          <PriorityBadge priority={task.priority} />
        </div>
        {task.labels && task.labels.length > 0 && (
          <div className="task-card-labels">
            Labels: {task.labels.join(', ')}
          </div>
        )}
        <div className="task-card-date">
          Due:{' '}
          <span className={isOverdue ? 'task-date-value is-overdue' : 'task-date-value'}>
            {formatTaskDate(task.dueDate, 'No due date')}
            {isOverdue && <span className="task-overdue-badge">Overdue</span>}
          </span>
        </div>
        <div className="task-card-date">
          Updated: {formatTaskDateTime(task.updatedAt, 'Not updated yet')}
        </div>
      </div>

      <div className="task-card-actions">
        <TaskActionButtons
          onViewDetails={() => onViewDetails(task)}
          onEdit={() => onEdit(task)}
          onDelete={() => {
            if (window.confirm('Are you sure you want to delete this task?')) {
              onDelete(task.id!);
            }
          }}
        />
      </div>
    </div>
  );
};
